/**
 * package-admin-resolve — item név/SKU feloldás a PackageAdmin read-DTO-hoz.
 *
 * BE-S1 measured-finding (Zeno): az item-DTO-ban NINCS referenceName/displayName — a nevek a
 * polimorf FK-n át jönnek: `productId` → a `linkedProducts[].product.{name,sku}`-ból resolve-olva.
 * (A rentalEquipmentId-hez a read-DTO még nem ad nevet → fallback a notes/itemType-ra.)
 *
 * STORY-1205 / ADR-118 Am.2
 */
import type { PackageAdmin, PackageAdminItem } from '@kgc/packages/package-admin';

export interface ResolvedItemRef {
  name: string;
  sku: string | null;
}

/**
 * Feloldja egy package-item megjelenítendő nevét/SKU-ját a package `linkedProducts[]`-ból.
 * - PRODUCT/ACCESSORY: a `productId` → linkedProducts product.name/sku
 * - RENTAL_EQUIPMENT: a read-DTO nem ad nevet → fallback (notes vagy itemType-címke)
 * - ismeretlen ref: fallback
 */
export function resolveItemRef(
  item: Pick<PackageAdminItem, 'productId' | 'itemType' | 'notes'>,
  linkedProducts: PackageAdmin['linkedProducts']
): ResolvedItemRef {
  if (item.productId) {
    const link = linkedProducts.find(lp => lp.productId === item.productId);
    if (link) {
      return { name: link.product.name, sku: link.product.sku };
    }
  }
  // RENTAL_EQUIPMENT / feloldatlan: a read-DTO-ból nincs név → notes vagy típus-fallback
  return { name: item.notes?.trim() || ITEM_TYPE_FALLBACK[item.itemType], sku: null };
}

const ITEM_TYPE_FALLBACK: Record<PackageAdminItem['itemType'], string> = {
  PRODUCT: 'Termék',
  RENTAL_EQUIPMENT: 'Bérgép',
  ACCESSORY: 'Tartozék',
  SERVICE_ITEM: 'Szolgáltatás',
};

/** A 3 csoport kanonikus sorrendje + magyar címkéje (proto: Ingyenes / Bérelhető / Bizományi). */
export const PACKAGE_ITEM_CATEGORIES = [
  { key: 'FREE', labelKey: 'package.category.free' as const, fallback: 'Ingyenes' },
  { key: 'RENTABLE', labelKey: 'package.category.rentable' as const, fallback: 'Bérelhető' },
  { key: 'CONSIGNMENT', labelKey: 'package.category.consignment' as const, fallback: 'Bizományi' },
] as const;

export type PackageItemCategoryKey = (typeof PACKAGE_ITEM_CATEGORIES)[number]['key'];

/** Csoportosítja az itemeket category szerint, sortOrder-rel rendezve a csoporton belül. */
export function groupItemsByCategory(
  items: PackageAdminItem[]
): Record<PackageItemCategoryKey, PackageAdminItem[]> {
  const groups: Record<PackageItemCategoryKey, PackageAdminItem[]> = {
    FREE: [],
    RENTABLE: [],
    CONSIGNMENT: [],
  };
  for (const item of items) {
    groups[item.category].push(item);
  }
  for (const key of Object.keys(groups) as PackageItemCategoryKey[]) {
    groups[key].sort((a, b) => a.sortOrder - b.sortOrder);
  }
  return groups;
}
